import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  BodyClasses,
  ColumnDefinition,
  ColumnDisplayType,
  ColumnSizes,
  GridComponent,
  GridConfiguration,
  OrderByPipe,
  DialogContent,
  FilterType
} from 'components';
import { map, take } from 'rxjs/operators';
import { DialogService } from 'src/app/core/services/dialog.service';
import { LocationsWrapperService } from 'src/app/core/services/service-wrappers/locations-wrapper.service';
import { LocationsDialogComponent } from './locations-dialog/locations-dialog.component';
import { LocationsService } from './locations.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { compare } from 'fast-json-patch';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { ImplementationWrapperService } from '@core/services/service-wrappers/implementation-wrapper.service';
import { Store } from '@ngrx/store';
import { ImplementationState } from '../../implementation/state/implementation.state';
import * as ImplementationActions from '../../implementation/state/implementation.actions';
import { selectImplementation } from '../../implementation/state/implementation.selectors';
import { ImplementationDetailViewModel } from 'projects/data/src/lib/model/implementationDetailViewModel';
import { ImplementationViewModel } from 'projects/data/src/lib/model/implementationViewModel';
import { UploadCodesComponent } from '../upload-codes/upload-codes.component';
import { SessionStorageService } from '@core/services/session-storage.service';
import { UserDetailsViewModel } from 'data';

@Component({
  standalone: false,
  selector: 'app-locations',
  templateUrl: './locations.component.html',
  styleUrls: ['./locations.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [LocationsService]
})
export class LocationsComponent implements OnInit, OnDestroy {
  @ViewChild(GridComponent) grid: GridComponent;
  activeFilter: string = 'active';
  orderPipe: OrderByPipe = new OrderByPipe();
  userInfo: UserDetailsViewModel;
  viewOnly = false;
  viewOnlyOptions = [{ id: 'view', label: 'View' }];
  overflowMenuOptions = [
    {
      id: 'edit',
      label: 'Edit'
    },
    {
      id: 'activate',
      label: 'Activate',
      field: 'inactive',
      condition: true
    },
    {
      id: 'inactivate',
      label: 'Inactivate',
      field: 'inactive',
      condition: false
    }
  ];
  private columnDefinitions: ColumnDefinition[] = [
    {
      id: 'actions',
      displayKey: 'actions',
      displayType: ColumnDisplayType.actionList,
      columnSize: ColumnSizes.tiny,
      bodyClasses: [BodyClasses.alignRight],
      displayOrder: 0,
      headerText: '',
      overflowMenuOptions: this.overflowMenuOptions,
      overflowMenuClicked: (value, data) => {
        if (value === 'edit') {
          this.openLocationModal(data.id);
        } else if (value === 'view') {
          this.openLocationModal(data.id, true);
        } else {
          this.setInactive(data, value === 'inactivate');
        }
      }
    },
    {
      id: 'code',
      displayKey: 'code',
      headerText: 'Code',
      displayOrder: 1,
      columnSize: ColumnSizes.extraNarrow,
      filterable: true,
      sortable: true,
      sticky: true
    },
    {
      id: 'name',
      displayKey: 'name',
      headerText: 'Name',
      displayOrder: 2,
      columnSize: ColumnSizes.normal,
      sortable: true,
      filterable: true
    },
    {
      id: 'address',
      displayKey: 'address1',
      headerText: 'Address',
      displayOrder: 3,
      columnSize: ColumnSizes.normal,
      bodyClasses: [BodyClasses.alignLeft],
      sortable: true,
      filterable: true
    },
    {
      id: 'city',
      displayKey: 'city',
      headerText: 'City',
      displayOrder: 4,
      columnSize: ColumnSizes.midwidth,
      bodyClasses: [BodyClasses.alignLeft],
      sortable: true,
      filterable: true
    },
    {
      id: 'state',
      displayKey: 'stateCode',
      headerText: 'State',
      displayOrder: 5,
      columnSize: ColumnSizes.fixedColumn,
      bodyClasses: [BodyClasses.alignLeft],
      sortable: true,
      filterable: true
    },
    {
      id: 'zip',
      displayKey: 'zip',
      headerText: 'Zip',
      displayOrder: 6,
      columnSize: ColumnSizes.narrow,
      bodyClasses: [BodyClasses.alignLeft],
      sortable: true,
      filterable: true
    },
    {
      id: 'npi',
      displayKey: 'npi',
      headerText: 'NPI',
      displayOrder: 7,
      columnSize: ColumnSizes.narrow,
      sortable: true,
      filterable: true
    }
  ];
  gridConfig: GridConfiguration = {
    columnDefinitions: this.columnDefinitions,
    displayColumns: this.orderPipe.transform(this.columnDefinitions, 'displayOrder').map((x) => x.id)
  };

  dataSource: LocationsService;
  constructor(
    private locationsService: LocationsService,
    private dialog: MatDialog,
    private wrapperService: LocationsWrapperService,
    private dialogService: DialogService,
    private notificationService: NotificationService,
    private activatedRoute: ActivatedRoute,
    private implementationService: ImplementationWrapperService,
    private router: Router,
    private store: Store<ImplementationState>,
    private sessionStorageService: SessionStorageService
  ) {
    this.userInfo = this.sessionStorageService.getItem('user-info');
    this.viewOnly =
      this.userInfo?.userPermissions?.find((x) => x.permissionType == 'SystemMaintenance')?.accessType !== 'Update';
    if (this.viewOnly) {
      this.columnDefinitions[0].overflowMenuOptions = this.viewOnlyOptions;
    }
    this.dataSource = locationsService;
    this.dataSource.activeFilter = 'active';
  }

  ngOnInit(): void {
    this.implementationSubscribe();
  }

  ngOnDestroy() {
    this.implementationUnsubscribe();
  }

  addLocation() {
    this.openLocationModal(null);
  }

  openLocationModal(locationId: string, viewOnly = false) {
    let activeLocs = [];
    let activeCodes = [];

    this.wrapperService
      .apiV1LocationAllPost({ pageSize: 99999, generalSearch: '' })
      .pipe(
        map((x: any) => {
          return x;
        })
      )
      .subscribe((data) => {
        activeLocs = data.data.filter((prov) => prov.id !== locationId);

        if (activeLocs !== null) {
          activeLocs.forEach((x) => {
            activeCodes.push(x.code.toString());
          });
        }

        const dialog = this.dialog.open(LocationsDialogComponent, {
          disableClose: true,
          autoFocus: false,
          data: { locationId, viewOnly, activeCodes }
        });

        dialog.afterClosed().subscribe((result) => {
          if (result) {
            this.grid.loadData();
          }
        });
      });
  }

  deleteEntry(data) {
    const modalContent: DialogContent = {
      header: 'Delete Location',
      body: `Are you sure you want to delete "${data.name}"? This cannot be undone.`,
      cancelButtonText: 'Cancel',
      OKButtonText: 'Delete'
    };
    this.dialogService.showConfirm(modalContent).subscribe((result) => {
      if (result) {
        this.wrapperService
          .apiV1LocationIdDelete(data.id)
          .pipe(
            map((x: any) => x),
            take(1)
          )
          .subscribe((deleteResult) => {
            this.grid.loadData();
            this.notificationService.notifyNow('Location Deleted');
          });
      }
    });
  }

  setInactive(data, setInactive = true) {
    const sTerminology: string = setInactive ? 'Inactivate' : 'Activate';

    const modalContent: DialogContent = {
      header: sTerminology + ' Location',
      body: `Are you sure you want to ` + sTerminology + ` "${data.name}"?`,
      cancelButtonText: 'Cancel',
      OKButtonText: sTerminology
    };
    this.dialogService.showConfirm(modalContent).subscribe((result) => {
      if (result) {
        if ((data.inactive && setInactive) || (data.inactive === false && setInactive === false)) {
          this.notificationService.notifyNow(data.name + ' is already ' + sTerminology + 'd');
        } else {
          const formData = {
            inactive: setInactive
          };
          this.wrapperService
            .apiV1LocationPatchIdPatch(data.id, compare({}, formData))
            .pipe(
              map((x: any) => x),
              take(1)
            )
            .subscribe((x) => {
              this.grid.loadData();
            });
        }
      }
    });
  }
  uploadCodes() {
    const dialog = this.dialog.open(UploadCodesComponent, {
      disableClose: true,
      autoFocus: false,
      height: '600px',
      width: '600px',
      data: { code: 'location' }
    });

    dialog.afterClosed().subscribe((result) => {
      this.grid.loadData();
    });
  }

  public isImplementation: boolean = false;
  public gonext: boolean = false;
  public itemCount: number = 0;
  private activatedRouteSub: Subscription;
  private itemCountSub: Subscription;
  private implementation$: Observable<ImplementationViewModel> = this.store.select(selectImplementation);
  private implementationSub: Subscription;
  private implementation: ImplementationViewModel = null;
  private implementationDetail: ImplementationDetailViewModel = null;
  private nextImplementationDetail: ImplementationDetailViewModel = null;
  private STEP_CODE = 'locations';

  implementationSubscribe() {
    this.activatedRouteSub = this.activatedRoute.data?.subscribe((data) => {
      this.isImplementation = data.implementation;

      if (this.isImplementation) {
        this.itemCountSub = this.locationsService.itemCountSubject?.subscribe((c) => (this.itemCount = c));
        this.implementationSub = this.implementation$?.subscribe((i) => {
          this.implementation = i;
          this.implementationDetail = this.implementation.details.find(
            (d) => d.implementationStepCode == this.STEP_CODE
          );
          this.nextImplementationDetail = this.implementation.details.find(
            (d) => d.order == this.implementationDetail.order + 1
          );
        });
      }
    });
    this.store.dispatch(ImplementationActions.updateImplementationStep({ stepCode: this.STEP_CODE }));
  }

  implementationUnsubscribe() {
    if (this.isImplementation) {
      this.activatedRouteSub.unsubscribe();
      this.itemCountSub.unsubscribe();
      this.implementationSub.unsubscribe();
    }
  }

  next(): void {
    if (this.implementationDetail.status == 'InProgress') {
      this.gonext = true;
      this.implementationService.apiV1ImplementationCompleteStepCodePost(this.STEP_CODE).subscribe(
        () => {
          this.gonext = false;
          this.router.navigateByUrl('/implementation/' + this.nextImplementationDetail.implementationStepCode);
        },
        () => (this.gonext = false)
      );
    } else {
      this.router.navigateByUrl('/implementation/' + this.nextImplementationDetail.implementationStepCode);
    }
  }
  showInactiveChanges(event) {
    this.activeFilter = event.checked ? 'active' : 'inactive';
    this.dataSource.activeFilter = this.activeFilter;
    this.grid.loadData();
  }
}
